/* eslint-disable arrow-parens */
// Card Asset Library — lets an admin upload/preview/delete the DB-backed
// images the Digital ID Card builder's AssetKeyPicker offers. One component
// serves both personas, discriminated purely by whether a `gn` route param
// is present: with it, this is the Employer's own group-scoped library
// (global assets ∪ this group's own, upload creates a group asset); without
// it, this is the Super Admin's global library (global assets only, upload
// creates a global asset) — matches getHealthCardAssets(group_number?)'s own
// "omitted -> global only, given -> global ∪ group" semantics.
import { useCallback, useContext, useEffect, useState } from "react";
import {
	Button,
	Grid,
	Paper,
	Typography,
	TextField,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Chip
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import { trackPromise } from "react-promise-tracker";

import { AuthContext, UIContext } from "../../../../contexts";
import { api } from "../../../../utils/api";
import useRouteParam from "../../../../hooks/useRouteParam";
import { ADMIN_DASHBOARD_HEADER } from "../../../../constants/caption/dashboardHeader";
import { getPermissionsFromGroupOwner } from "../../../../utils/getPermissionsFromGroupOwner";
import { HealthCardAsset } from "../../../../@types/healthCardConfig.types";

const AssetLibrary = () => {
	const groupNumber = useRouteParam("gn") as number | null;
	const { user } = useContext(AuthContext);
	const { setDashboardHeader } = useContext(UIContext);

	const [assets, setAssets] = useState<HealthCardAsset[]>([]);
	const [loading, setLoading] = useState(false);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [file, setFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [assetKey, setAssetKey] = useState("");
	const [label, setLabel] = useState("");
	const [saving, setSaving] = useState(false);

	const permission = groupNumber
		? user
			? getPermissionsFromGroupOwner(user, Number(groupNumber))?.employer_settings_menu?.digital_id_card_sub?.self
			: null
		: null;

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.health_card_asset_library);
	}, [setDashboardHeader]);

	const loadAssets = useCallback(async () => {
		setLoading(true);
		try {
			const result = await trackPromise(api.healthCardAsset.getHealthCardAssets(groupNumber ?? undefined));
			setAssets(result);
		} finally {
			setLoading(false);
		}
	}, [groupNumber]);

	useEffect(() => {
		loadAssets();
	}, [loadAssets]);

	// Revoke the object URL when it's replaced/unmounted rather than leaking
	// it — cheap, but there's no reason not to clean it up.
	useEffect(() => {
		return () => {
			if (previewUrl) URL.revokeObjectURL(previewUrl);
		};
	}, [previewUrl]);

	const resetDialog = () => {
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		setFile(null);
		setPreviewUrl(null);
		setAssetKey("");
		setLabel("");
	};

	const openDialog = () => {
		resetDialog();
		setDialogOpen(true);
	};

	const closeDialog = () => {
		setDialogOpen(false);
		resetDialog();
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0] ?? null;
		if (previewUrl) URL.revokeObjectURL(previewUrl);
		setFile(selected);
		setPreviewUrl(selected ? URL.createObjectURL(selected) : null);
	};

	const handleUpload = async () => {
		if (!file || !assetKey.trim() || !label.trim()) return;
		setSaving(true);
		try {
			const formData = new FormData();
			formData.append("attachment", file);
			formData.append(
				"health_card_asset",
				JSON.stringify({
					asset_key: assetKey.trim(),
					label: label.trim(),
					...(groupNumber ? { group_number: Number(groupNumber) } : {})
				})
			);
			await api.healthCardAsset.uploadHealthCardAsset(formData);
			closeDialog();
			await loadAssets();
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (asset: HealthCardAsset) => {
		if (!window.confirm(`Delete "${asset.label}" (${asset.asset_key})? This can't be undone.`)) return;
		await api.healthCardAsset.deleteHealthCardAsset(asset.id);
		await loadAssets();
	};

	if (permission && !permission.view) {
		return <Typography style={{ textAlign: "center", padding: 24 }}>No view permission</Typography>;
	}

	return (
		<Grid container spacing={2} style={{ padding: 16 }}>
			<Grid item xs={12} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<Typography variant="h6">{groupNumber ? "This Group's Assets" : "Global Card Assets"}</Typography>
				{(!permission || permission.update) && (
					<Button
						variant="contained"
						color="primary"
						startIcon={<AddIcon />}
						onClick={openDialog}
						style={{ color: "#fff" }}
					>
						Upload Asset
					</Button>
				)}
			</Grid>

			{loading ? (
				<Grid item xs={12} style={{ textAlign: "center", padding: 24 }}>
					<CircularProgress size={28} />
				</Grid>
			) : assets.length === 0 ? (
				<Grid item xs={12}>
					<Typography color="textSecondary" style={{ padding: 24, textAlign: "center" }}>
						No assets uploaded yet.
					</Typography>
				</Grid>
			) : (
				assets.map((asset) => (
					<Grid item xs={6} sm={4} md={3} lg={2} key={asset.id}>
						<Paper variant="outlined" style={{ padding: 12, textAlign: "center", position: "relative" }}>
							{(!permission || permission.update) && (
								<IconButton
									size="small"
									onClick={() => handleDelete(asset)}
									style={{ position: "absolute", top: 4, right: 4 }}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							)}
							<img
								src={asset.url}
								alt={asset.label}
								style={{ width: "100%", height: 64, objectFit: "contain", marginTop: 8 }}
							/>
							<Typography variant="body2" style={{ marginTop: 8, wordBreak: "break-word" }}>
								{asset.label}
							</Typography>
							<Typography variant="caption" color="textSecondary" style={{ wordBreak: "break-word" }}>
								{asset.asset_key}
							</Typography>
							<div style={{ marginTop: 4 }}>
								<Chip
									size="small"
									label={asset.scope === "global" ? "Global" : "This Group"}
									color={asset.scope === "global" ? "default" : "primary"}
								/>
							</div>
						</Paper>
					</Grid>
				))
			)}

			<Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
				<DialogTitle>Upload Asset</DialogTitle>
				<DialogContent>
					<input
						id="asset-library-file-input"
						type="file"
						accept="image/png, image/jpeg, image/svg+xml"
						onChange={handleFileChange}
						style={{ display: "none" }}
					/>
					<label htmlFor="asset-library-file-input">
						<Button variant="outlined" component="span" fullWidth style={{ marginTop: 8 }}>
							{file ? file.name : "Choose Image"}
						</Button>
					</label>
					{previewUrl && (
						<div style={{ textAlign: "center", margin: "16px 0" }}>
							<img src={previewUrl} alt="preview" style={{ maxWidth: "100%", maxHeight: 160 }} />
						</div>
					)}
					<TextField
						label="Label"
						placeholder='e.g. "PHCS Network Logo"'
						value={label}
						onChange={(event) => setLabel(event.target.value)}
						fullWidth
						variant="outlined"
						margin="dense"
					/>
					<TextField
						label="Asset Key"
						placeholder='e.g. "phcs_logo"'
						value={assetKey}
						onChange={(event) => setAssetKey(event.target.value)}
						fullWidth
						variant="outlined"
						margin="dense"
						helperText="Unique identifier referenced by card configs — must match exactly if replacing an existing key."
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog} disabled={saving}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleUpload}
						disabled={saving || !file || !assetKey.trim() || !label.trim()}
						style={{ color: "#fff" }}
					>
						{saving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};

export default AssetLibrary;
